import type { CompositionV3Ent } from '@revelio/data-access';

import { compositionV3EntityKey } from '../../../../filters/utils/composition-v3/getCompositionV3Entities';
import type { CompositionV3AnyGraphqlResponse } from './composition-v3-graphql-response';

type CompositionV3ResponseEntity =
  CompositionV3AnyGraphqlResponse['entities'][number];

const createEmptyEntity = (
  id: number,
  name: string
): CompositionV3ResponseEntity =>
  ({
    __typename: 'CompositionV3RespEnt',
    id,
    short_name: name,
    long_name: name,
    metrics: {
      __typename: 'CompositionV3RespData',
      headcount: [],
      tenure: [],
      salary: [],
      growth_rate: [],
      hiring_rate: [],
      attrition_rate: [],
      gender: [],
      ethnicity: [],
      role: [],
      geography: [],
      seniority: [],
      education: [],
      rics: [],
      skills: [],
    },
  }) as CompositionV3ResponseEntity;

/**
 * Backfills entities the composition API omitted from its response so charts
 * render every requested primary entity — missing ones surface as empty
 * "no data" instead of silently disappearing.
 */
export const amendCompositionV3WithMissingEntities = <
  TData extends { composition_v3?: CompositionV3AnyGraphqlResponse | null },
>({
  data,
  requestedEntities,
  entityNamesByKey,
}: {
  data: TData | undefined;
  requestedEntities: readonly CompositionV3Ent[];
  entityNamesByKey?: ReadonlyMap<string, string>;
}): TData | undefined => {
  const response = data?.composition_v3;

  if (
    !response ||
    !Array.isArray(response.entities) ||
    response.entities.length >= requestedEntities.length
  ) {
    return data;
  }

  const returnedEntitiesByRequestIndex = new Map(
    response.entities.map((entity) => [entity.id, entity])
  );

  let missingCount = 0;

  const orderedEntities = requestedEntities.map((ent, index) => {
    const expectedName =
      entityNamesByKey?.get(compositionV3EntityKey(ent)) ?? '';

    const match = returnedEntitiesByRequestIndex.get(index);
    if (match) {
      return { ...match, id: ent.EntID };
    }

    missingCount++;
    return createEmptyEntity(ent.EntID, expectedName);
  });

  if (missingCount === 0) return data;

  return {
    ...data,
    composition_v3: {
      ...response,
      entities: orderedEntities,
    },
  };
};
